import { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Eye } from '@phosphor-icons/react';
import OrderDetailDrawer from './OrderDetailDrawer';
import { useOrders } from '../../hooks/useOrders';
import { useUser } from '../../hooks/useUser';
import TableSkeleton from './TableSkeleton';
import NoDataFallback from '../common/NoDataFallback';

const CurrentOrders = () => {
  const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
  const { user } = useUser();
  const showInr =
    user?.tenantName === 'cure_food' || user?.tenantName === 'nik_bakers';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const { data, isLoading, error, isError } = useOrders({
    skip: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    startDate: today,
    endDate: today,
    tenantName: user?.tenantName,
    userId: user?._id || '',
  });

  const paymentMethod = (paymentMethod: string) => {
    paymentMethod = paymentMethod.replace(/_/g, ' ');
    return paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
  };

  // Transform API data to match the expected format for the DataGrid
  const transformedOrders =
    data?.data?.items
      ?.map((order: any, index: number) => {
        const orderDate = new Date(order.created_at);
        const formattedDateTime = orderDate
          .toLocaleString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'UTC',
          })
          .replace('at', '');

        return {
          // INTERNAL ID: Used by React/DataGrid for tracking (must be unique)
          id: `${order.id || order.order_no || 'unknown'}-${order.created_at}-${index}`,

          // DISPLAY ID: This is what shows in the "Order Id" column (exact API value)
          orderNo: order.id || order.order_no || `ORD-${index}`,

          dateTime: formattedDateTime,
          orderDate: orderDate,
          customerName: order.customer_info?.name || 'Unknown',
          amount: order.total_price || 0,
          paymentMethod: paymentMethod(
            order.payment?.payment_method || 'Unknown'
          ),
          paymentStatus: order.payment?.status || 'pending',
          orderStatus: order.status || 'placed',
          // Store original data for reference
          originalId: order.id || order.order_no,
          originalOrder: order,
        };
      })
      ?.sort(
        (a: any, b: any) => b.orderDate.getTime() - a.orderDate.getTime()
      ) || [];

  const handleViewDetails = (id: string) => {
    const order = transformedOrders.find((order: any) => order.id === id);
    if (order) {
      // Use the stored original order data
      setSelectedOrder(order.originalOrder);
      setDrawerOpen(true);
    }
  };

  // Common color functions to ensure consistency
  const getStatusBackgroundColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'completed':
      case 'delivered':
        return 'rgba(40, 167, 69, 0.15)'; // Success green background

      case 'acknowledged':
        return 'rgba(40, 167, 69, 0.15)';
      case 'dispatched':
        return 'rgba(40, 167, 69, 0.15)';
      case 'pending':
        return 'rgba(255, 193, 7, 0.15)'; // Warning yellow background

      case 'failed':
      case 'cancelled':
        return 'rgba(220, 53, 69, 0.15)'; // Danger red background

      case 'refunded':
        return 'rgba(108, 117, 125, 0.15)'; // Secondary gray background

      case 'out_for_delivery':
        return 'rgba(13, 110, 253, 0.15)'; // Primary blue background

      case 'preparing':
        return 'rgba(255, 193, 7, 0.15)'; // Warning yellow background

      case 'ready':
        return 'rgba(102, 16, 242, 0.15)'; // Purple background

      case 'confirmed':
        return 'rgba(23, 162, 184, 0.15)'; // Info cyan background

      default:
        return 'rgba(108, 117, 125, 0.15)'; // Default gray background
    }
  };

  const getStatusTextColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'completed':
      case 'delivered':
        return '#28a745'; // Success green text

      case 'acknowledged':
        return '#28a745'; // Success green text
      case 'dispatched':
        return '#28a745'; // Success green text
      case 'pending':
        return '#ffc107'; // Warning yellow text

      case 'failed':
      case 'cancelled':
        return '#dc3545'; // Danger red text

      case 'refunded':
        return '#6c757d'; // Secondary gray text

      case 'out_for_delivery':
        return '#0d6efd'; // Primary blue text

      case 'preparing':
        return '#ffc107'; // Warning yellow text

      case 'ready':
        return '#6610f2'; // Purple text

      case 'confirmed':
        return '#17a2b8'; // Info cyan text

      default:
        return '#6c757d'; // Default gray text
    }
  };

  // Payment status color functions that use the common functions
  const getPaymentStatusColor = (status: string) => {
    return getStatusBackgroundColor(status);
  };

  const getPaymentTextColor = (status: string) => {
    return getStatusTextColor(status);
  };

  // Order status color functions that use the common functions
  const getOrderStatusColor = (status: string) => {
    return getStatusBackgroundColor(status);
  };

  const getOrderTextColor = (status: string) => {
    return getStatusTextColor(status);
  };

  const columns: GridColDef[] = [
    {
      field: 'orderNo',
      headerName: 'Order Id',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'dateTime',
      headerName: 'Date & Time',
      width: 180,
      headerClassName: 'custom-header',
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) =>
        `${parseFloat(params.value || 0).toFixed(2)} ${showInr ? 'Rs.' : '$'}`,
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment Method',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value || 'pending';
        return (
          <Box
            sx={{
              backgroundColor: getPaymentStatusColor(status.toString()),
              color: getPaymentTextColor(status.toString()),
              width: 78,
              height: 24,
              borderRadius: '6px',
              padding: '4px 8px',
              fontWeight: '400',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}
          >
            {status.toString().charAt(0).toUpperCase() +
              status.toString().slice(1)}
          </Box>
        );
      },
    },
    {
      field: 'orderStatus',
      headerName: 'Order Status',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value || 'placed';
        return (
          <Box
            sx={{
              backgroundColor: getOrderStatusColor(status.toString()),
              color: getOrderTextColor(status.toString()),
              width: 'auto',
              minWidth: 78,
              maxWidth: 130,
              height: 24,
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: '400',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {status
              .toString()
              .split('_')
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
              )
              .join(' ')}
          </Box>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Order Details',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="View Details">
          <IconButton
            onClick={() => handleViewDetails(params.row.id as string)}
            size="small"
          >
            <Eye />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (isLoading) {
    return <TableSkeleton />;
  }

  // Get total count from API response (handle both response structures)
  const totalCount = data?.data?.total || 0;

  if (data?.data?.items?.length === 0 && !isLoading) {
    return (
      <NoDataFallback
        message="No orders are available for today"
        description="Looks like there are no orders to display right now. Once you start receiving orders, they'll show up here"
      />
    );
  }

  if (isError && error && !isLoading) {
    return (
      <NoDataFallback
        message="No orders are available for today"
        description="Looks like there are no orders to display right now. Once you start receiving orders, they'll show up here"
      />
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={transformedOrders}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50, 75, 100]}
        checkboxSelection={false}
        disableRowSelectionOnClick
        // Add server-side pagination support
        paginationMode="server"
        rowCount={totalCount}
        sx={{
          borderRadius: '8px',
          color: 'rgba(107, 111, 128, 1)',
          '& .MuiDataGrid-root': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-main': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            // padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .custom-header': {
            bgcolor: '#F8F9FC',
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 600,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: 'rgba(43, 45, 63, 1)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#E7F0FF !important',
          },
          '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
        }}
      />

      {/* Order Detail Drawer */}
      <OrderDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        order={selectedOrder}
        getPaymentStatusColor={getPaymentStatusColor}
        getPaymentTextColor={getPaymentTextColor}
        getOrderStatusColor={getOrderStatusColor}
        getOrderTextColor={getOrderTextColor}
      />
    </Box>
  );
};

export default CurrentOrders;
